import { useApolloClient } from "@apollo/client";
import { CardContainer, CollapseSection, ModalOpenButton } from "@msys/ui";
import {
  AssignmentInd as ContactsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { Link } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { RestrictedByProjectPermissionWithDebug } from "../../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ProfileListItemButton } from "../../../commons/ProfileListItem.js";
import {
  ALLOW_INVITE_EXISTING_CONTRACTEE,
  ALLOW_INVITE_NEW_CONTRACTEE,
} from "../../../constants.js";
import { CrmCompanyEditModal } from "../../crm-companies/CrmCompanyEditModal.js";
import { useCancelProjectContracteeInvitationMutation } from "../../crm-persons/boxes/CrmPersonPendingContracteeInvitationsBox.generated.js";
import { CrmPersonEditModal } from "../../crm-persons/CrmPersonEditModal.js";
import { CrmSendEmailModal } from "../../crm/CrmSendEmailModal.js";
import { InvitationStatusBadge } from "../../invitations/InvitationStatusBadge.js";
import { MyOrganisationUserEditModal } from "../../organisations/MyOrganisationUserEditModal.js";
import { OrganisationAvatar } from "../../organisations/OrganisationAvatar.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import { useProjectRoles } from "../../users/useRoles.js";
import { AddProjectMemberButton } from "../buttons/AddProjectMemberButton.js";
import { AddProjectMemberCRMButton } from "../buttons/AddProjectMemberCRMButton.js";
import { ModifyProjectMembersButton } from "../buttons/ModifyProjectMembersButton.js";
import { ProjectAssigneeSelectFormWithAutoSave } from "../ProjectAssigneeSelect.js";
import { ProjectTeamsSection } from "./ProjectTeamsSection.js";
import {
  ProjectOverviewParticipantsBox_ProjectFragment,
  RowContracteeCompany_CrmCompanyFragment,
  RowContracteeCompany_ProjectFragment,
  RowContracteeMember_CrmPersonFragment,
  RowContracteeMember_ProjectContracteeInvitationFragment,
  RowContracteeMember_ProjectFragment,
  RowContractor_CrmCompanyFragment,
  RowInvitingContractor_CrmCompanyFragment,
  RowMember_ProjectMembershipFragment,
} from "./ProjectOverviewParticipantsBox.generated.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";

interface Props {
  project: ProjectOverviewParticipantsBox_ProjectFragment;
}

export const ProjectOverviewParticipantsBox = ({ project }: Props) => {
  const { t } = useTranslate("ProjectOverview");
  const viewer = useUserData().currentUser!;

  const viewerOrganisation = viewer.organisation;
  const crmOrganisation = project.crmOrganisation;
  const showContractee =
    viewerOrganisation.capabilities.includes("quoting") && crmOrganisation;
  const canInviteContractee = project.canInviteContractee;

  return (
    <CardContainer
      id="project-overview-participants-box"
      Icon={<ContactsIcon />}
      title={t("Project participants")}
      isExpandable
    >
      <RestrictedByProjectPermissionWithDebug
        permission="project_project_update"
        project={project}
        otherwise={
          <ProjectAssigneeSelectFormWithAutoSave
            projectId={project.id}
            readOnly={true}
          />
        }
      >
        <ProjectAssigneeSelectFormWithAutoSave projectId={project.id} />
      </RestrictedByProjectPermissionWithDebug>
      <Box
        sx={{
          paddingX: 1,
        }}
      >
        <CollapseSection
          title={t("My team")}
          itemCount={project.internalStakeholders.length}
          ActionButtons={[
            <RestrictedByProjectPermissionWithDebug
              key="manage-participants"
              permission="project_project_update"
              project={project}
            >
              <ModifyProjectMembersButton
                projectId={project.id}
                type="icon"
                refetchQueries={[namedOperations.Query.ProjectOverview]}
              />
            </RestrictedByProjectPermissionWithDebug>,
            <RestrictedByProjectPermissionWithDebug
              key="add-member"
              permission="project_member_create"
              project={project}
            >
              <AddProjectMemberButton
                projectId={project.id}
                refetchQueries={[namedOperations.Query.ProjectOverview]}
              />
            </RestrictedByProjectPermissionWithDebug>,
          ]}
        >
          <List disablePadding sx={{ marginX: -1 }}>
            {project.internalStakeholders.map(m => (
              <RowMyTeamMember key={m.id} membership={m} />
            ))}
          </List>
        </CollapseSection>
        <RestrictedByCapabilityWithDebug capability="teams">
          <ProjectTeamsSection project={project} />
          <Divider />
        </RestrictedByCapabilityWithDebug>
        {showContractee && (
          <>
            <Divider />
            <CollapseSection title={t("Contractee")}>
              <List disablePadding sx={{ marginX: -1 }}>
                <RowContracteeCompany
                  canInvite={ALLOW_INVITE_NEW_CONTRACTEE && canInviteContractee}
                  project={project}
                  crmOrganisation={crmOrganisation}
                />
                {crmOrganisation.members.map(member => {
                  const invitation = project.contracteeInvitations.find(
                    invitation => invitation.contact.id === member.id
                  );
                  return (
                    <RowContracteeMember
                      key={member.id}
                      person={member}
                      invitation={invitation}
                      project={project}
                      organisationId={crmOrganisation.id}
                      canInvite={
                        ALLOW_INVITE_EXISTING_CONTRACTEE && canInviteContractee
                      }
                    />
                  );
                })}
              </List>
            </CollapseSection>
          </>
        )}
        {project.invitingContractor &&
          !project.contractors.some(
            c => c.id === project.invitingContractor?.id
          ) && (
            <>
              <Divider />
              <CollapseSection title={t("Inviting contractor")}>
                <List disablePadding sx={{ marginX: -1 }}>
                  <RowInvitingContractor
                    invitingContractor={project.invitingContractor}
                  />
                </List>
              </CollapseSection>
            </>
          )}
        <Divider />
        <CollapseSection
          title={t("Contractors")}
          itemCount={project.contractors.length}
        >
          <List disablePadding sx={{ marginX: -1 }}>
            {project.contractors.map(contractor => (
              <RowContractor key={contractor.id} contractor={contractor} />
            ))}
          </List>
        </CollapseSection>
      </Box>
    </CardContainer>
  );
};

const RowMyTeamMember = ({
  membership,
}: {
  membership: RowMember_ProjectMembershipFragment;
}) => {
  const viewer = useUserData().currentUser!;
  const { getProjectRoleTitle } = useProjectRoles();

  let primaryLabel =
    `${membership.user.firstname} ${membership.user.familyname}`.trim();
  if (primaryLabel.trim().length === 0) primaryLabel = membership.user.email;
  if (membership.user.id === viewer.id) primaryLabel += " (me)";

  const roleLabel = getProjectRoleTitle(membership.roles);

  return (
    <ModalOpenButton
      Modal={MyOrganisationUserEditModal}
      modalProps={{ userId: membership.user.id }}
    >
      <ProfileListItemButton
        avatar={<UserAvatar userAvatar={membership.user} />}
        primary={primaryLabel}
        secondary={roleLabel}
        ActionButton={
          membership.user.phones.length ? (
            <IconButton
              color="primary"
              size="small"
              component="a"
              href={`tel:${membership.user.phones[0].number}`}
            >
              <PhoneIcon />
            </IconButton>
          ) : undefined
        }
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: `/organisation/users/${membership.user.id}`,
        }}
      />
    </ModalOpenButton>
  );
};

const RowInvitingContractor = ({
  invitingContractor,
}: {
  invitingContractor: RowInvitingContractor_CrmCompanyFragment;
}) => {
  return (
    <ModalOpenButton
      Modal={CrmCompanyEditModal}
      modalProps={{ crmCompanyId: invitingContractor.id }}
    >
      <ProfileListItemButton
        size="s"
        avatar={
          <OrganisationAvatar
            size="s"
            organisationAvatar={invitingContractor}
          />
        }
        primary={invitingContractor.title}
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: `/crm/organisations/${invitingContractor.id}`,
        }}
      />
    </ModalOpenButton>
  );
};

const RowContractor = ({
  contractor,
}: {
  contractor: RowContractor_CrmCompanyFragment;
}) => {
  return (
    <ModalOpenButton
      Modal={CrmCompanyEditModal}
      modalProps={{ crmCompanyId: contractor.id }}
    >
      <ProfileListItemButton
        size="s"
        avatar={<OrganisationAvatar size="s" organisationAvatar={contractor} />}
        primary={contractor.title}
        listItemProps={{
          component: Link,
          to: `/crm/organisations/${contractor.id}`,
        }}
      />
    </ModalOpenButton>
  );
};

const RowContracteeCompany = ({
  project,
  crmOrganisation,
  canInvite,
}: {
  canInvite: boolean;
  project: RowContracteeCompany_ProjectFragment;
  crmOrganisation: RowContracteeCompany_CrmCompanyFragment;
}) => {
  const context = React.useMemo(
    () =>
      ({
        type: "PROJECT",
        id: project.id,
      }) as const,
    [project.id]
  );

  return (
    <ModalOpenButton
      Modal={CrmCompanyEditModal}
      modalProps={{ crmCompanyId: crmOrganisation.id }}
    >
      <ProfileListItemButton
        size="s"
        avatar={
          <OrganisationAvatar size="s" organisationAvatar={crmOrganisation} />
        }
        primary={crmOrganisation.title}
        ActionButton={
          <Stack direction="row" spacing={1}>
            {crmOrganisation.email && (
              <ModalOpenButton
                Modal={CrmSendEmailModal}
                modalProps={{
                  emailTemplateContext: "PROJECT",
                  context,
                  recipientOptions: [
                    {
                      key: crmOrganisation.id,
                      crmCompanyId: crmOrganisation.id,
                      crmPersonId: null,
                      label: `${crmOrganisation.title} <${crmOrganisation.email}>`,
                      value: crmOrganisation.id,
                    },
                  ],
                  recipient: {
                    key: crmOrganisation.id,
                    crmCompanyId: crmOrganisation.id,
                    crmPersonId: null,
                    label: `${crmOrganisation.title} <${crmOrganisation.email}>`,
                    value: crmOrganisation.id,
                  },
                  projectId: project.id,
                }}
              >
                <IconButton color="primary">
                  <EmailIcon />
                </IconButton>
              </ModalOpenButton>
            )}
            {canInvite ? (
              <RestrictedByProjectPermissionWithDebug
                permission="project_project_update"
                project={project}
              >
                <AddProjectMemberCRMButton
                  projectId={project.id}
                  refetchQueries={[namedOperations.Query.ProjectOverview]}
                />
              </RestrictedByProjectPermissionWithDebug>
            ) : undefined}
          </Stack>
        }
        listItemProps={{
          onClick: e => e.preventDefault(), // in order to open modal
          component: Link,
          to: `/crm/organisations/${crmOrganisation.id}`,
        }}
      />
    </ModalOpenButton>
  );
};

const RowContracteeMember = ({
  organisationId,
  project,
  person,
  invitation,
  canInvite,
}: {
  organisationId: string;
  project: RowContracteeMember_ProjectFragment;
  person: RowContracteeMember_CrmPersonFragment;
  invitation?: RowContracteeMember_ProjectContracteeInvitationFragment;
  canInvite: boolean;
}) => {
  const { t } = useTranslate("ProjectOverview");
  const name = `${person.firstname} ${person.familyname}`.trim();

  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [cancelProjectContracteeInvitation] =
    useCancelProjectContracteeInvitationMutation({ client });

  const context = React.useMemo(
    () =>
      ({
        type: "PROJECT",
        id: project.id,
      }) as const,
    [project.id]
  );

  const handleInvitationCancel = invitation
    ? async () => {
        await cancelProjectContracteeInvitation({
          variables: {
            projectId: project.id,
            projectContracteeInvitationId: invitation.id,
          },
          awaitRefetchQueries: true,
          refetchQueries: [namedOperations.Query.ProjectOverview],
        });
        enqueueSnackbar(t("Invitation cancelled"), {
          variant: "info",
        });
      }
    : undefined;

  return (
    <ModalOpenButton
      Modal={CrmPersonEditModal}
      modalProps={{ crmPersonId: person.id, crmCompanyId: organisationId }}
    >
      <ProfileListItemButton
        avatar={<UserAvatar userAvatar={person} />}
        primary={name}
        badge={
          invitation ? (
            <InvitationStatusBadge
              style={{ opacity: 0.6 }}
              status={invitation.status}
            />
          ) : undefined
        }
        ActionButtons={[
          canInvite && invitation ? (
            <RestrictedByProjectPermissionWithDebug
              key={"cancel-invitation"}
              permission="project_project_update"
              project={project}
            >
              <Tooltip
                style={{ pointerEvents: "all" }}
                title={t("Cancel invitation")}
              >
                <IconButton color="primary" onClick={handleInvitationCancel}>
                  <UndoIcon />
                </IconButton>
              </Tooltip>
            </RestrictedByProjectPermissionWithDebug>
          ) : undefined,
          person.email && (
            <ModalOpenButton
              key={"send-email"}
              Modal={CrmSendEmailModal}
              modalProps={{
                emailTemplateContext: "PROJECT",
                context,
                recipientOptions: [
                  {
                    key: person.id,
                    crmCompanyId: organisationId,
                    crmPersonId: person.id,
                    label: `${person.fullname} <${person.email}>`,
                    value: person.id,
                  },
                ],
                recipient: {
                  key: person.id,
                  crmCompanyId: organisationId,
                  crmPersonId: person.id,
                  label: `${person.fullname} <${person.email}>`,
                  value: person.id,
                },
                projectId: project.id,
              }}
            >
              <IconButton color="primary">
                <EmailIcon />
              </IconButton>
            </ModalOpenButton>
          ),
        ].filter(Boolean)}
        RightButton={
          canInvite && !invitation ? (
            <RestrictedByProjectPermissionWithDebug
              permission="project_project_update"
              project={project}
            >
              <AddProjectMemberCRMButton
                title={t("Invite")}
                projectId={project.id}
                userId={person.id}
                style={{ pointerEvents: "all" }}
                refetchQueries={[namedOperations.Query.ProjectOverview]}
                Button={
                  <Button
                    color="primary"
                    variant="outlined"
                    size="extra-small"
                    style={{ pointerEvents: "all" }}
                  >
                    {t("Invite")}
                  </Button>
                }
              />
            </RestrictedByProjectPermissionWithDebug>
          ) : undefined
        }
      />
    </ModalOpenButton>
  );
};
